import { API_BASE_URL, handleResponse, getAuthHeaders, apiClient } from '@/lib/api-client';

export interface Session {
  session_id: string;
  story_id: string;
  student_id: string;
  started_at: string;
}

export interface SessionSubmission {
  session_id: string;
  answers: Answer[];
  time_spent_reading_seconds: number;
  quiz_started_at: string;
}

export interface Answer {
  question_id: string;
  selected_answer: string;
  time_spent_seconds: number;
}

export interface SessionResult {
  session_id: string;
  score: number;
  correct_count: number;
  total_questions: number;
  feedback: QuestionFeedback[];
}

export interface QuestionFeedback {
  question_id: string;
  is_correct: boolean;
  selected_answer: string;
  correct_answer: string;
  explanation: string;
}

export interface SessionDetail {
  id: string;
  score: number;
  total_questions: number;
  completed_at: string;
  started_at: string;
  time_spent_reading_seconds: number | null;
  quiz_started_at: string | null;
  quiz_completed_at: string | null;
  story_id: string;
  story_title: string;
  story_theme: string | null;
  reflection: string | null;
  feedback: Array<{
    question_id: string;
    question: string;
    student_answer: string;
    correct_answer: string;
    is_correct: boolean;
    reason: string;
    focus: string;
    options?: Record<string, string>;
  }>;
}

export const sessionsApi = {
  async startSession(studentId: string, storyId: string): Promise<Session> {
    const response = await fetch(`${API_BASE_URL}/sessions/start`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        student_id: studentId,
        story_id: storyId,
      }),
    });

    return handleResponse<Session>(response, 'Failed to start session');
  },

  async submitSession(data: SessionSubmission): Promise<SessionResult> {
    const response = await fetch(`${API_BASE_URL}/sessions/submit`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    return handleResponse<SessionResult>(response, 'Failed to submit session');
  },

  async getSessionDetail(sessionId: string): Promise<SessionDetail> {
    const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<SessionDetail>(response, 'Failed to fetch session details');
  },

  async getSessionHistory(): Promise<Session[]> {
    const response = await fetch(`${API_BASE_URL}/sessions/history`, {
      headers: getAuthHeaders(),
    });

    return handleResponse<Session[]>(response, 'Failed to fetch session history');
  },

  async sessionHeartbeat(sessionId: string): Promise<void> {
    return apiClient.patch(`/sessions/${sessionId}/heartbeat`);
  },
};
